import axios from "axios";
import axiosInstance from "@/axiosConfig";
import { computeMD5Base64 } from "@/shared/utils/image.utils";

type DirectUploadResponse = {
  signed_id: string
  key: string
  filename: string
  content_type: string
  byte_size: number
  checksum: string
  service_name: string
  direct_upload: {
    url: string
    access_url: string
    headers: {
      "Content-Type": string
      "Content-MD5": string
      "Content-Disposition": string
    }
  }
};

export type UploadResult = {
  signedId: string
  publicUrl: string
};

const HTTP_STATUS_MULTIPLE_CHOICES = 300;

const uploadToS3 = async (
  file: File,
  directUpload: DirectUploadResponse["direct_upload"],
  onUploadProgress?: (percent: number) => void,
): Promise<void> => {
  const { url, headers } = directUpload;
  const response = await axios.put(url, file, {
    headers,
    onUploadProgress: (progressEvent) => {
      if (onUploadProgress && progressEvent.total) {
        onUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      }
    },
  });

  if (response.status < 200 || response.status >= HTTP_STATUS_MULTIPLE_CHOICES) {
    throw new Error(
      "Nous n'avons pas réussi à enregistrer votre image, veuillez réessayer",
    );
  }
};

export const postFile = async (
  file: File,
  directUploadUrl?: string,
  onUploadProgress?: (percent: number) => void,
): Promise<UploadResult> => {
  try {
    const uploadUrl = directUploadUrl || "/direct_uploads";
    const response = await axiosInstance.post<DirectUploadResponse>(
      uploadUrl,
      {
        blob: {
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
          checksum: await computeMD5Base64(file),
        },
      },
    );

    const { signed_id, direct_upload } = response.data;
    await uploadToS3(file, direct_upload, onUploadProgress);

    // Get the public URL from the direct_upload access_url
    const publicUrl = direct_upload.access_url;

    return {
      signedId: signed_id,
      publicUrl: publicUrl,
    };
  }
  catch (error) {
    throw new Error(
      "Nous n'avons pas réussi à enregistrer votre fichier, veuillez réessayer",
      { cause: error },
    );
  }
};

/**
 * Generate public URL for uploaded image using signed_id (deprecated)
 * Use fetchBlobPublicUrl for new implementations
 * @deprecated Use postFile and get publicUrl from result, or use fetchBlobPublicUrl directly
 */
export const generateImageUrl = (signedId: string): string => {
  // Option 1: If you serve images through your API
  return `/api/uploads/${signedId}`;

  // Option 2: If you have a direct S3 URL pattern (update with your actual bucket/region)
  // return `https://your-bucket-name.s3.your-region.amazonaws.com/${signedId}`;

  // Option 3: If you have a CDN (update with your CDN domain)
  // return `https://your-cdn-domain.com/${signedId}`;
};

/**
 * Backward compatibility function for old postFile interface
 * Returns only the signedId like the old version
 */
export const postFileSignedId = async (file: File): Promise<string> => {
  const result = await postFile(file);
  return result.signedId;
};
